import { unpack } from '@carboncopyco/rrweb';
import type { eventWithTime } from '@carboncopyco/rrweb-types';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import { Debug } from '@carboncopyco/utils';
import { gunzipSync, gzipSync, strFromU8, strToU8 } from 'fflate';
import { isEqual } from 'lodash-es';

const debug = Debug.extend('mimesis-sdk:compression');
const sdkLogger = scopedLogger(Scope.MIMESIS_SDK);

export const getCompressedEvents = (
  events: eventWithTime[] | string,
  errorHandler: (error: string) => void
) => {
  if (events?.length === 0) {
    return;
  }
  const compressedEvents = `[${gzipSync(
    strToU8(
      encodeURIComponent(
        typeof events !== 'string' ? JSON.stringify(events) : events
      )
    )
  ).toString()}]`;
  debug('Compressed events', compressedEvents);

  const compressionVerified = verifyCompression(
    typeof events === 'string' ? JSON.parse(events) : events,
    JSON.parse(compressedEvents),
    errorHandler
  );

  debug('Compression verified', compressionVerified);

  if (compressionVerified) {
    return compressedEvents;
  }
  return events;
};

const verifyCompression = (
  originalEvents: eventWithTime[] | string,
  compressedEvents: number[],
  errorHandler: (error: string) => void
) => {
  try {
    const uncompressedEvents = getGzipUncompressedEvents(compressedEvents);
    return !!isEqual(originalEvents, uncompressedEvents);
  } catch (error) {
    sdkLogger.error(
      'Error verifying compression',
      { originalEvents, compressedEvents },
      error
    );
    errorHandler(error);
    return false;
  }
};

const getGzipUncompressedEvents = (
  compressedEvents: (string | number | object)[]
) => {
  const uncompressedEventsPerFrame = gunzipSync(
    Uint8Array.from(compressedEvents as number[])
  );
  try {
    return JSON.parse(
      decodeURIComponent(strFromU8(uncompressedEventsPerFrame))
    );
  } catch (_) {
    return JSON.parse(strFromU8(uncompressedEventsPerFrame));
  }
};

export const getUncompressedEvents = (
  compressedEvents: (string | number | object)[][]
) => {
  const uncompressedEvents = compressedEvents?.map(
    (compressedEventsPerFrame) => {
      const isCompressedWithPack =
        typeof compressedEventsPerFrame?.[0] === 'string';
      const isCompressedWithGzip =
        typeof compressedEventsPerFrame?.[0] === 'number';
      const isNotCompressed = typeof compressedEventsPerFrame?.[0] === 'object';

      debug('Compressed with: ', {
        isCompressedWithPack,
        isCompressedWithGzip,
        isNotCompressed,
      });

      if (isNotCompressed) {
        return compressedEventsPerFrame;
      }
      if (isCompressedWithPack) {
        return compressedEventsPerFrame.map(
          (compressedEvent) =>
            unpack(compressedEvent as string) as eventWithTime
        );
      }
      if (isCompressedWithGzip) {
        return getGzipUncompressedEvents(compressedEventsPerFrame);
      }

      return [];
    }
  );

  debug('uncompressedEvents: ', { uncompressedEvents });

  return uncompressedEvents;
};
